<template>
  <transition name="back-to-top">
    <button
      v-show="isVisible"
      ref="button"
      type="button"
      class="btn-to-top"
      :style="buttonStyle"
      :tabindex="isVisible ? 0 : -1"
      :aria-hidden="String(!isVisible)"
      aria-label="ページ上部へ戻る"
      @click="scrollToTop"
    >
      <i class="fa fa-chevron-up" aria-hidden="true"></i>
    </button>
  </transition>
</template>

<script>
const MIN_SCROLL_THRESHOLD_PX = 480;
const VIEWPORT_THRESHOLD_RATIO = 0.75;
const VISIBILITY_HYSTERESIS_PX = 80;
const SCROLL_DIRECTION_THRESHOLD_PX = 4;
const COMPACT_VIEWPORT_MAX_WIDTH_PX = 1199;
const SCROLL_TO_TOP_FALLBACK_MS = 1200;

export default {
  name: "BackToTop",
  data: () => ({
    footerOverlap: 0,
    hasPassedThreshold: false,
    isScrollingToTop: false,
    isScrollingUp: false,
    isVisible: false,
    lastScrollY: 0,
    scrollDirectionDelta: 0,
    scrollFrameId: null,
    scrollToTopTimerId: null,
  }),
  computed: {
    buttonStyle() {
      return {
        "--back-to-top-footer-offset": `${this.footerOverlap}px`,
      };
    },
  },
  watch: {
    $route() {
      this.resetVisibility();
      this.$nextTick(() => this.requestUpdate());
    },
  },
  methods: {
    focusMainContent() {
      const mainContent = document.getElementById("main-content");

      if (mainContent) {
        try {
          mainContent.focus({ preventScroll: true });
        } catch (error) {
          mainContent.focus();
        }
      }
    },
    scrollToTop() {
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      this.focusMainContent();

      this.isScrollingToTop = true;
      this.setVisibility(false);
      window.clearTimeout(this.scrollToTopTimerId);

      if (prefersReducedMotion) {
        window.scrollTo(0, 0);
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }

      this.scrollToTopTimerId = window.setTimeout(() => {
        this.isScrollingToTop = false;
        this.requestUpdate();
      }, SCROLL_TO_TOP_FALLBACK_MS);
    },
    getFooterOverlap() {
      const footer = document.querySelector(".app-footer");
      if (!footer) {
        return 0;
      }

      const footerRect = footer.getBoundingClientRect();
      const visibleHeight = window.innerHeight - footerRect.top;

      return Math.round(
        Math.min(footerRect.height, Math.max(0, visibleHeight))
      );
    },
    getScrollThreshold() {
      return Math.max(
        MIN_SCROLL_THRESHOLD_PX,
        window.innerHeight * VIEWPORT_THRESHOLD_RATIO
      );
    },
    requestUpdate() {
      if (this.scrollFrameId !== null) {
        return;
      }

      this.scrollFrameId = window.requestAnimationFrame(() => {
        this.scrollFrameId = null;
        this.updateState();
      });
    },
    resetVisibility() {
      this.hasPassedThreshold = false;
      this.isScrollingToTop = false;
      this.isScrollingUp = false;
      this.scrollDirectionDelta = 0;
      this.setVisibility(false);
      this.lastScrollY = Math.max(window.scrollY || window.pageYOffset || 0, 0);
      window.clearTimeout(this.scrollToTopTimerId);
      this.scrollToTopTimerId = null;
    },
    setVisibility(isVisible) {
      if (
        !isVisible &&
        this.isVisible &&
        this.$refs.button === document.activeElement
      ) {
        this.focusMainContent();
      }

      this.isVisible = isVisible;
    },
    updateState() {
      const currentScrollY = Math.max(
        window.scrollY || window.pageYOffset || 0,
        0
      );
      const scrollDelta = currentScrollY - this.lastScrollY;
      const scrollThreshold = this.getScrollThreshold();
      const hideThreshold = Math.max(
        0,
        scrollThreshold - VISIBILITY_HYSTERESIS_PX
      );
      const availableScrollDistance = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight
      );
      const isCompactViewport =
        window.innerWidth <= COMPACT_VIEWPORT_MAX_WIDTH_PX;

      const hasChangedDirection =
        (scrollDelta > 0 && this.scrollDirectionDelta < 0) ||
        (scrollDelta < 0 && this.scrollDirectionDelta > 0);

      if (hasChangedDirection) {
        this.scrollDirectionDelta = 0;
      }

      this.scrollDirectionDelta += scrollDelta;

      if (
        Math.abs(this.scrollDirectionDelta) >=
        SCROLL_DIRECTION_THRESHOLD_PX
      ) {
        this.isScrollingUp = this.scrollDirectionDelta < 0;
        this.scrollDirectionDelta = 0;
      }

      this.lastScrollY = currentScrollY;
      this.footerOverlap = this.getFooterOverlap();

      if (this.hasPassedThreshold) {
        this.hasPassedThreshold = currentScrollY > hideThreshold;
      } else {
        this.hasPassedThreshold =
          availableScrollDistance > scrollThreshold &&
          currentScrollY >= scrollThreshold;
      }

      if (this.isScrollingToTop) {
        this.setVisibility(false);

        if (currentScrollY <= 1) {
          this.isScrollingToTop = false;
          this.isScrollingUp = false;
          this.scrollDirectionDelta = 0;
          window.clearTimeout(this.scrollToTopTimerId);
          this.scrollToTopTimerId = null;
        }
        return;
      }

      const hasReturnIntent =
        !isCompactViewport || this.isScrollingUp || this.footerOverlap > 0;

      this.setVisibility(this.hasPassedThreshold && hasReturnIntent);
    },
  },
  mounted() {
    this.lastScrollY = Math.max(window.scrollY || window.pageYOffset || 0, 0);
    this.requestUpdate();
    window.addEventListener("scroll", this.requestUpdate, { passive: true });
    window.addEventListener("resize", this.requestUpdate, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.requestUpdate);
    window.removeEventListener("resize", this.requestUpdate);
    window.clearTimeout(this.scrollToTopTimerId);

    if (this.scrollFrameId !== null) {
      window.cancelAnimationFrame(this.scrollFrameId);
    }
  },
};
</script>

<style lang="scss" scoped>
.btn-to-top {
  --back-to-top-edge-offset: 16px;

  position: fixed;
  right: calc(
    var(--back-to-top-edge-offset) + env(safe-area-inset-right, 0px)
  );
  bottom: calc(
    var(--back-to-top-edge-offset) +
      env(safe-area-inset-bottom, 0px) +
      var(--back-to-top-footer-offset, 0px)
  );
  z-index: 20;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: white;
  background-color: rgba(128, 128, 128, 0.7);
  box-shadow: 0 4px 14px rgba(33, 28, 26, 0.18);
  font-size: 24px;
  line-height: 1;
  text-align: center;
  cursor: pointer;
  appearance: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.btn-to-top:focus-visible {
  outline: 3px solid white;
  outline-offset: 3px;
  box-shadow: 0 0 0 6px #211c1a;
}

.fa {
  color: currentColor;
}

.back-to-top-enter-active {
  transition:
    opacity 0.24s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.24s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: opacity, transform;
}

.back-to-top-leave-active {
  pointer-events: none;
  transition:
    opacity 0.16s cubic-bezier(0.4, 0, 1, 1),
    transform 0.16s cubic-bezier(0.4, 0, 1, 1);
  will-change: opacity, transform;
}

.back-to-top-enter,
.back-to-top-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

@media screen and (min-width: 600px) {
  .btn-to-top {
    --back-to-top-edge-offset: 24px;
  }
}

@media screen and (min-width: 1200px) {
  .btn-to-top {
    --back-to-top-edge-offset: 32px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .back-to-top-enter-active,
  .back-to-top-leave-active {
    transition: none;
  }
}
</style>
